import React, {memo, useContext, useEffect, useRef, useState} from "react";
import Currency from "@autocx/forms/src/controls/currency";
import AnimateHeight from "react-animate-height";
import {WebsiteContext} from "../../index";
import classNames from "classnames";
import {aspectRatio} from "../../utils/block-utils";
import {Icons} from "./components/card-icons";
import BidCountdown from "./components/bid-countdown";
import {AUCTIONSTATUSENUM, getStatus} from "../product-block";
import WebsiteFieldRenderer from "../../components/website-field-renderer";
import {toUriSegment} from "../../utils/utils";
import ProductCardMediaCarousel from "./components/media-carousel";
import ProductCardAction, {getProductCardActionConfig} from "./components/card-action";
import {subscribeToPublicSalesEvents} from "../../realtime/public-sales-events";
import useAuctionConnection, {isAuctionConnectionInterrupted} from '../../realtime/use-auction-connection';
import axios from "axios";
import {
    appendCatalogueNumberQuery,
    fetchPublicCatalogueLotSummary,
    getPublicProductIdentifier,
    getPublicProductRouteIdentifier,
    isPublicSalesEventMessageForCurrentLot,
    isPublicSalesEventStateCurrent,
    mergePublicSalesEventSummary,
    mergePublicProductProps,
    mergeRefreshedPublicProduct,
    sanitizePublicProduct
} from "../../realtime/public-sales-event-product-state";
import {
    getAuctionOpenBidStatusLabel,
    getAuctionReserveMetLabel,
    getOpenAuctionBidStatus,
    OPEN_AUCTION_BID_STATUS
} from "../product-block/bid-status";

function hasViewerBidLookup(product) {
    return !!product && Object.prototype.hasOwnProperty.call(product, 'viewerBid');
}

function normaliseViewerBid(bid) {
    if (!bid || bid.amount === null || bid.amount === undefined) return null;

    return {
        amount: bid.amount,
        bidPlaced: bid.bidPlaced,
        isWinner: !!bid.isWinner
    };
}

const VIEWER_BID_LAG_RETRY_DELAY_MS = 1200;
const CARD_STATUS_ENTER_MS = 220;
const CARD_STATUS_EXIT_MS = 180;

function getAuctionStatusDividerClassName(tone) {
    return tone === 'success'
        ? "auction-card-status-line--success"
        : tone === 'warning'
            ? "auction-card-status-line--warning"
            : "auction-card-status-line--neutral";
}

function getAuctionStatusChipClassName(tone) {
    return tone === 'success'
        ? "border-emerald-300/90"
        : tone === 'warning'
            ? "border-rose-300/90"
            : "border-transparent";
}

function renderCardBidStatusDot(tone) {
    if (tone === 'success') {
        return <span className="inline-flex h-2 w-2 shrink-0 rounded-full bg-emerald-400 status-dot-calm"/>;
    }

    if (tone === 'warning') {
        return (
            <span className="relative inline-flex h-2.5 w-2.5 items-center justify-center">
                <span className="absolute inline-flex h-full w-full rounded-full bg-rose-200"/>
                <span className="absolute inline-flex h-full w-full rounded-full bg-rose-400/90 status-dot-alert"/>
                <span className="relative inline-flex h-1 w-1 rounded-full bg-rose-700"/>
            </span>
        );
    }

    return null;
}

export const Design1 = memo(({product, id, ...props}) => {
    const [liveProduct, setLiveProduct] = useState(() => sanitizePublicProduct(product));
    const context = useContext(WebsiteContext)
    const publicProductIdentifier = getPublicProductIdentifier(
        liveProduct,
        getPublicProductIdentifier(product, id)
    );
    const publicProductRouteIdentifier = getPublicProductRouteIdentifier(
        liveProduct,
        getPublicProductRouteIdentifier(product, id)
    );
    const salesEvent = liveProduct?.salesEvent;
    const startDate = salesEvent?.startDate;
    const endDate = salesEvent?.endDate;
    const isAuction = salesEvent?.sellingMethod?.toLowerCase() === 'auction';
    const saleLabel = isAuction ? 'Auction' : 'Tender';
    const [secondsRemaining, setSecondsRemaining] = useState(salesEvent?.secondsRemaining)
    const [secondsToStart, setSecondsToStart] = useState(salesEvent?.secondsToStart)

    const [status, setStatus] = useState(() => getStatus(salesEvent, startDate, endDate, secondsToStart, secondsRemaining));
    const nowRef = useRef(Date.now());
    const refreshTimeoutRef = useRef(null);
    const viewerBidRefreshTimeoutRef = useRef(null);
    const latestSummaryRef = useRef(null);
    const salesEventRef = useRef(salesEvent);
    const catalogueNumberRef = useRef(liveProduct?.catalogueContext?.catalogueNumber);
    const viewerBidLookupEnabled = hasViewerBidLookup(liveProduct);
    const customerAuthenticated = !!context?.customerSession?.isAuthenticated;
    const publicProductIdentifierRef = useRef(publicProductIdentifier);
    const viewerBidLookupEnabledRef = useRef(viewerBidLookupEnabled);
    const customerAuthenticatedRef = useRef(customerAuthenticated);
    const previousCustomerAuthenticatedRef = useRef(customerAuthenticated);
    const viewerBidAuthRejectedRef = useRef(false);
    const viewerBidRequestSequenceRef = useRef(0);
    const publicRefreshSequenceRef = useRef(0);
    const subscriptionSellingMethod = product?.salesEvent?.sellingMethod?.toLowerCase() ?? null;
    const incomingLotId = product?.salesEvent?.lotId ?? null;
    const subscriptionLotId = subscriptionSellingMethod === 'auction' ? incomingLotId : null;
    const requestIdentity = JSON.stringify([
        getPublicProductIdentifier(product, id),
        subscriptionSellingMethod,
        incomingLotId,
        product?.catalogueContext?.catalogueNumber
    ]);
    const requestIdentityRef = useRef(requestIdentity);
    requestIdentityRef.current = requestIdentity;
    const [now, setNow] = useState(nowRef.current);
    const lotOutcome = String(salesEvent?.lotStatus || '').trim().toLowerCase().replace(/[\s_-]+/g, '');
    const isPassedIn = lotOutcome === 'passedin';
    const isSaleFellThrough = lotOutcome === 'salefellthrough';
    const hasFinalNoWinnerOutcome = isPassedIn || isSaleFellThrough;
    const isWithdrawnOrCancelled = ['withdrawn', 'cancelled', 'canceled'].includes(lotOutcome);
    const displayStatus = hasFinalNoWinnerOutcome ? AUCTIONSTATUSENUM.ENDED : status;
    const isFinalisedStatus = displayStatus === AUCTIONSTATUSENUM.ENDED || displayStatus === AUCTIONSTATUSENUM.CLOSED;
    const isNotStarted = displayStatus === AUCTIONSTATUSENUM.NOT_STARTED;
    const reserveMet = !!salesEvent?.reserveMet && (salesEvent?.totalBids ?? 0) > 0;
    const reserveMetLabel = getAuctionReserveMetLabel(salesEvent);
    const connectionState = useAuctionConnection(isAuction ? [{productUuid: publicProductIdentifier, lotId: subscriptionLotId}] : []);
    const connectionInterrupted = isAuction && !isWithdrawnOrCancelled && !hasFinalNoWinnerOutcome
        && displayStatus !== AUCTIONSTATUSENUM.CLOSED && isAuctionConnectionInterrupted(connectionState);
    const bidSummaryLabel = displayStatus === AUCTIONSTATUSENUM.CLOSED || hasFinalNoWinnerOutcome || isWithdrawnOrCancelled
        || (isFinalisedStatus && (salesEvent?.totalBids ?? 0) === 0) ? 'Final bid' : connectionInterrupted ? 'Last known bid' : 'Current bid';
    const emptyBidSummary = 'No bids yet';
    const totalBids = salesEvent?.totalBids ?? 0;
    const hasAnyBid = totalBids > 0 || Number(salesEvent?.highestBid) > 0
        || (liveProduct?.viewerBid?.amount !== null && liveProduct?.viewerBid?.amount !== undefined);
    const bidCountLabel = `${totalBids} bid${totalBids === 1 ? '' : 's'}`;
    const showOpenBidPrompt = isAuction && !connectionInterrupted && displayStatus === AUCTIONSTATUSENUM.OPEN && totalBids === 0;
    const openBidStatus = getOpenAuctionBidStatus({
        isAuction,
        status: displayStatus,
        usersBidAmount: liveProduct?.viewerBid?.amount,
        highestBidAmount: salesEvent?.highestBid
    });
    const cardBidStatusLabel = getAuctionOpenBidStatusLabel(openBidStatus, salesEvent);
    const showCardBidStatus = isAuction && !connectionInterrupted && displayStatus === AUCTIONSTATUSENUM.OPEN && !!cardBidStatusLabel;
    const cardBidStatusTone = openBidStatus === OPEN_AUCTION_BID_STATUS.LEADING
        ? 'success'
        : openBidStatus === OPEN_AUCTION_BID_STATUS.OUTBID
            ? 'warning'
            : 'neutral';
    const showAuctionStatusDivider = isAuction && displayStatus === AUCTIONSTATUSENUM.OPEN;
    const [renderedCardBidStatus, setRenderedCardBidStatus] = useState(() => showCardBidStatus
        ? {
            key: `${openBidStatus}:${cardBidStatusLabel}`,
            label: cardBidStatusLabel,
            tone: cardBidStatusTone
        }
        : null
    );
    const [cardBidStatusPhase, setCardBidStatusPhase] = useState(showCardBidStatus ? 'enter' : 'hidden');
    const cardBidStatusTimeoutRef = useRef(null);
    const showNoBidsReceivedSummary = isAuction && isFinalisedStatus && !hasAnyBid;
    const showReserveStrip = isAuction && reserveMet && displayStatus === AUCTIONSTATUSENUM.OPEN;
    const shouldAnimateReserveBadge = showReserveStrip;
    const eventEndTime = endDate ? new Date(endDate).getTime() : null;
    const isTenderInstantWin = !isAuction && displayStatus === AUCTIONSTATUSENUM.CLOSED && eventEndTime && eventEndTime > now;

    const hasResolvedWinner = !hasFinalNoWinnerOutcome && displayStatus === AUCTIONSTATUSENUM.CLOSED;
    const hasPendingOutcome = displayStatus === AUCTIONSTATUSENUM.ENDED && !hasFinalNoWinnerOutcome
        && !isWithdrawnOrCancelled && !hasResolvedWinner;

    let statusMessage = null;
    let statusMessageClassName = "";
    if (isSaleFellThrough) {
        statusMessage = "Sale Cancelled";
    } else if (hasResolvedWinner) {
        statusMessage = isTenderInstantWin ? "Instantly Won" : "Sold";
    } else if (isAuction && displayStatus === AUCTIONSTATUSENUM.ENDED && (isWithdrawnOrCancelled || !hasAnyBid)) {
        statusMessage = 'Auction Ended';
    } else if (displayStatus === AUCTIONSTATUSENUM.ENDED && isPassedIn) {
        statusMessage = `${saleLabel} ended with no winner`;
    } else if (hasPendingOutcome) {
        statusMessage = isAuction && totalBids > 0 ? 'Closing…' : `${saleLabel} ended`;
    }

    const catalogueQuery = salesEvent?.catalogueNumber
        ? `?catalogue=${encodeURIComponent(salesEvent.catalogueNumber)}`
        : '';
    const href = `product/${publicProductRouteIdentifier}/${toUriSegment(liveProduct?.title)}${catalogueQuery}`;
    const cardAction = getProductCardActionConfig(liveProduct);

    useEffect(() => {
        nowRef.current = Date.now();
        setNow(nowRef.current);
    }, []);

    useEffect(() => {
        publicProductIdentifierRef.current = publicProductIdentifier;

        if (!previousCustomerAuthenticatedRef.current && customerAuthenticated) {
            viewerBidAuthRejectedRef.current = false;
        }
        previousCustomerAuthenticatedRef.current = customerAuthenticated;

        if (viewerBidLookupEnabled && !viewerBidAuthRejectedRef.current) {
            viewerBidLookupEnabledRef.current = true;
        }
        customerAuthenticatedRef.current = customerAuthenticated && !viewerBidAuthRejectedRef.current;
    }, [publicProductIdentifier, viewerBidLookupEnabled, customerAuthenticated]);

    useEffect(() => {
        salesEventRef.current = salesEvent;
        catalogueNumberRef.current = liveProduct?.catalogueContext?.catalogueNumber;
    }, [salesEvent, liveProduct?.catalogueContext?.catalogueNumber]);

    useEffect(() => {
        const nextCardBidStatus = showCardBidStatus
            ? {
                key: `${openBidStatus}:${cardBidStatusLabel}`,
                label: cardBidStatusLabel,
                tone: cardBidStatusTone
            }
            : null;

        if (cardBidStatusTimeoutRef.current) {
            clearTimeout(cardBidStatusTimeoutRef.current);
            cardBidStatusTimeoutRef.current = null;
        }

        if (nextCardBidStatus) {
            if (!renderedCardBidStatus) {
                setRenderedCardBidStatus(nextCardBidStatus);
                setCardBidStatusPhase('enter');
                cardBidStatusTimeoutRef.current = setTimeout(() => {
                    setCardBidStatusPhase('idle');
                    cardBidStatusTimeoutRef.current = null;
                }, CARD_STATUS_ENTER_MS);
                return;
            }

            if (
                renderedCardBidStatus.key !== nextCardBidStatus.key
                || renderedCardBidStatus.label !== nextCardBidStatus.label
                || renderedCardBidStatus.tone !== nextCardBidStatus.tone
            ) {
                setCardBidStatusPhase('exit');
                cardBidStatusTimeoutRef.current = setTimeout(() => {
                    setRenderedCardBidStatus(nextCardBidStatus);
                    setCardBidStatusPhase('enter');
                    cardBidStatusTimeoutRef.current = setTimeout(() => {
                        setCardBidStatusPhase('idle');
                        cardBidStatusTimeoutRef.current = null;
                    }, CARD_STATUS_ENTER_MS);
                }, CARD_STATUS_EXIT_MS);
                return;
            }

            if (cardBidStatusPhase === 'exit' || cardBidStatusPhase === 'hidden') {
                setCardBidStatusPhase('enter');
                cardBidStatusTimeoutRef.current = setTimeout(() => {
                    setCardBidStatusPhase('idle');
                    cardBidStatusTimeoutRef.current = null;
                }, CARD_STATUS_ENTER_MS);
                return;
            }

            if (cardBidStatusPhase === 'enter') {
                cardBidStatusTimeoutRef.current = setTimeout(() => {
                    setCardBidStatusPhase('idle');
                    cardBidStatusTimeoutRef.current = null;
                }, CARD_STATUS_ENTER_MS);
            }
            return;
        }

        if (renderedCardBidStatus) {
            setCardBidStatusPhase('exit');
            cardBidStatusTimeoutRef.current = setTimeout(() => {
                setRenderedCardBidStatus(null);
                setCardBidStatusPhase('hidden');
                cardBidStatusTimeoutRef.current = null;
            }, CARD_STATUS_EXIT_MS);
            return;
        }

        setCardBidStatusPhase('hidden');
    }, [cardBidStatusLabel, cardBidStatusPhase, cardBidStatusTone, openBidStatus, renderedCardBidStatus, showCardBidStatus]);

    useEffect(() => {
        setStatus(() => getStatus(salesEvent, startDate, endDate, secondsToStart, secondsRemaining));
    }, [salesEvent, startDate, endDate, secondsToStart, secondsRemaining]);

    useEffect(() => {
        setLiveProduct((currentProduct) => mergePublicProductProps(currentProduct, product));
    }, [product]);

    useEffect(() => {
        requestIdentityRef.current = requestIdentity;
        latestSummaryRef.current = null;
        viewerBidAuthRejectedRef.current = false;
        return () => {
            if (requestIdentityRef.current === requestIdentity) requestIdentityRef.current = null;
            publicRefreshSequenceRef.current += 1;
            viewerBidRequestSequenceRef.current += 1;
            if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
            if (viewerBidRefreshTimeoutRef.current) clearTimeout(viewerBidRefreshTimeoutRef.current);
        };
    }, [requestIdentity]);

    useEffect(() => {
        nowRef.current = Date.now();
        setNow(nowRef.current);
        setSecondsRemaining(salesEvent?.secondsRemaining ?? 0);
        setSecondsToStart(salesEvent?.secondsToStart ?? 0);
    }, [salesEvent?.lotId, salesEvent?.endDate, salesEvent?.secondsRemaining, salesEvent?.secondsToStart]);

    function refreshSalesEventState() {
        if (requestIdentityRef.current !== requestIdentity) return;
        const productIdentifier = publicProductIdentifierRef.current;
        if (!productIdentifier) return;
        const requestSequence = ++publicRefreshSequenceRef.current;
        const catalogueNumber = catalogueNumberRef.current;
        const lotId = salesEventRef.current?.lotId;
        const isCurrentRequest = () => requestIdentityRef.current === requestIdentity && requestSequence === publicRefreshSequenceRef.current;

        axios.get(`/api/v2/products/${productIdentifier}`)
            .then(async ({data}) => {
                if (!isCurrentRequest()) return;
                let catalogueSummary = null;

                if (catalogueNumber) {
                    try {
                        catalogueSummary = await fetchPublicCatalogueLotSummary({
                            catalogueNumber,
                            lotId,
                            productIdentifier
                        });
                    } catch (error) {
                        console.log(error);
                    }
                }

                if (!isCurrentRequest()) return;

                setLiveProduct((currentProduct) => mergeRefreshedPublicProduct(
                    currentProduct,
                    data,
                    catalogueSummary ?? latestSummaryRef.current
                ));
                refreshViewerBidState({
                    force: customerAuthenticatedRef.current,
                    retryOnPotentialLag: customerAuthenticatedRef.current
                });
            })
            .catch((error) => console.log(error));
    }

    function refreshViewerBidState({force = false, retryOnPotentialLag = false} = {}) {
        if (requestIdentityRef.current !== requestIdentity) return;
        const productIdentifier = publicProductIdentifierRef.current;
        if (
            !productIdentifier
            || viewerBidAuthRejectedRef.current
            || (!viewerBidLookupEnabledRef.current && !force)
        ) return;

        const viewerBidsPath = appendCatalogueNumberQuery(
            `/api/v2/publicusers/profile/bids?pageNumber=1&pageSize=1&productIds=${encodeURIComponent(productIdentifier)}`,
            catalogueNumberRef.current
        );
        const requestSequence = ++viewerBidRequestSequenceRef.current;
        const isCurrentRequest = () => requestIdentityRef.current === requestIdentity && requestSequence === viewerBidRequestSequenceRef.current;
        axios.get(viewerBidsPath)
            .then(({data}) => {
                if (!isCurrentRequest() || viewerBidAuthRejectedRef.current) return;

                const latestBid = Array.isArray(data) ? data[0] : null;
                const latestBidAmount = latestBid?.amount === null || latestBid?.amount === undefined
                    ? null
                    : Number(latestBid.amount);
                const summaryHighestBid = latestSummaryRef.current?.highestBid === null || latestSummaryRef.current?.highestBid === undefined
                    ? null
                    : Number(latestSummaryRef.current.highestBid);
                const bidMayBeBehindSummary = !latestBid
                    || (Number.isFinite(latestBidAmount) && Number.isFinite(summaryHighestBid) && latestBidAmount < summaryHighestBid);

                setLiveProduct((currentProduct) => sanitizePublicProduct({
                    ...currentProduct,
                    viewerBid: normaliseViewerBid(latestBid)
                }));
                if (retryOnPotentialLag && bidMayBeBehindSummary) {
                    scheduleViewerBidRefresh({
                        force,
                        delay: VIEWER_BID_LAG_RETRY_DELAY_MS,
                        retryOnPotentialLag: false
                    });
                }
            })
            .catch((error) => {
                if (!isCurrentRequest()) return;
                if (error?.response?.status === 401 || error?.response?.status === 403) {
                    viewerBidAuthRejectedRef.current = true;
                    viewerBidLookupEnabledRef.current = false;
                    customerAuthenticatedRef.current = false;
                    viewerBidRequestSequenceRef.current += 1;
                    if (viewerBidRefreshTimeoutRef.current) {
                        clearTimeout(viewerBidRefreshTimeoutRef.current);
                        viewerBidRefreshTimeoutRef.current = null;
                    }
                    setLiveProduct((currentProduct) => sanitizePublicProduct({
                        ...currentProduct,
                        viewerBid: null
                    }));
                    return;
                }
                if (requestSequence !== viewerBidRequestSequenceRef.current) return;
                console.log(error);
            });
    }

    function scheduleRefresh() {
        if (refreshTimeoutRef.current) {
            clearTimeout(refreshTimeoutRef.current);
        }

        refreshTimeoutRef.current = setTimeout(() => {
            refreshSalesEventState();
        }, 150 + Math.round(Math.random() * 150));
    }

    function scheduleViewerBidRefresh({force = false, delay = 300, retryOnPotentialLag = false} = {}) {
        if (viewerBidRefreshTimeoutRef.current) {
            clearTimeout(viewerBidRefreshTimeoutRef.current);
        }

        viewerBidRefreshTimeoutRef.current = setTimeout(() => {
            viewerBidRefreshTimeoutRef.current = null;
            refreshViewerBidState({force, retryOnPotentialLag});
        }, delay);
    }

    useEffect(() => {
        if (!publicProductIdentifier || !product?.salesEvent) return;

        return subscribeToPublicSalesEvents(publicProductIdentifier, {
            lotId: subscriptionLotId,
            onSummary: (message) => {
                const summary = message?.summary;
                if (requestIdentityRef.current !== requestIdentity) return;
                if (!summary || !isPublicSalesEventMessageForCurrentLot(salesEventRef.current, summary)) return;
                if (!isPublicSalesEventStateCurrent(salesEventRef.current, summary)) return;
                if (latestSummaryRef.current && !isPublicSalesEventStateCurrent(latestSummaryRef.current, summary)) return;
                const previousSummary = latestSummaryRef.current ?? salesEventRef.current;
                const viewerBidMayHaveChanged = !latestSummaryRef.current || ['highestBid', 'totalBids', 'lotStatus'].some((key) =>
                    Object.prototype.hasOwnProperty.call(summary, key) && summary[key] !== previousSummary?.[key]);
                latestSummaryRef.current = summary;

                setLiveProduct((currentProduct) => sanitizePublicProduct({
                    ...currentProduct,
                    salesEvent: mergePublicSalesEventSummary(currentProduct?.salesEvent, summary)
                }));
                if (viewerBidMayHaveChanged) {
                    scheduleViewerBidRefresh({
                        force: customerAuthenticatedRef.current,
                        retryOnPotentialLag: customerAuthenticatedRef.current
                    });
                }
            },
            onInvalidation: (message) => {
                if (!isPublicSalesEventMessageForCurrentLot(salesEventRef.current, message)) return;
                if (!isPublicSalesEventStateCurrent(salesEventRef.current, message)) return;
                scheduleRefresh();
            },
            onReconnect: () => {
                scheduleRefresh();
            }
        });
    }, [publicProductIdentifier, subscriptionSellingMethod, subscriptionLotId, viewerBidLookupEnabled, requestIdentity]);

    useEffect(() => () => {
        if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
        if (viewerBidRefreshTimeoutRef.current) clearTimeout(viewerBidRefreshTimeoutRef.current);
        if (cardBidStatusTimeoutRef.current) clearTimeout(cardBidStatusTimeoutRef.current);
    }, []);

    function renderAuctionCountdown({suffix = null} = {}) {
        return (
            <BidCountdown
                salesEvent={liveProduct.salesEvent}
                date={now + (secondsRemaining * 1000)}
                showSeconds={false}
                showSecondsUnderMinutes={60}
                boldUnderMinutes={60}
                suffix={suffix}
                className="tabular-nums"
                onComplete={() => {
                    if (requestIdentityRef.current !== requestIdentity || salesEventRef.current?.endDate !== endDate) return;
                    // Add code to trigger the next bid count down seconds remaining.
                    nowRef.current = Date.now();
                    setNow(nowRef.current);
                    setSecondsRemaining(0)
                    setStatus(AUCTIONSTATUSENUM.ENDED);
                }}
            />
        );
    }

    function renderAuctionPriceSummary() {
        if (!isAuction || isNotStarted) return null;

        const hasBidAmount = salesEvent?.highestBid > 0;
        const summaryValue = showNoBidsReceivedSummary
            ? 'No bids received'
            : hasBidAmount
                ? (
                    <Currency.DisplayValue
                        currency={context.currency}
                        country={context.country}
                        value={salesEvent.highestBid}
                        emptyPlaceholder={""}
                    />
                )
                : emptyBidSummary;

        return (
            <div className="mt-1">
                <span className="block text-[11px] font-bold uppercase leading-tight text-gray-600">
                    {bidSummaryLabel}
                </span>
                <span className={classNames(
                    "block font-bold leading-tight text-gray-900 tabular-nums",
                    hasBidAmount ? "text-xl" : "text-base"
                )}>
                    {summaryValue}
                </span>
            </div>
        );
    }

    function renderAuctionSecondarySummary() {
        if (!isAuction || isNotStarted) return null;

        return (
            <div className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm text-gray-900">
                <span className={classNames(
                    showOpenBidPrompt ? "font-semibold" : "tabular-nums"
                )}>
                    {showOpenBidPrompt ? 'Place a bid' : bidCountLabel}
                </span>
                {displayStatus === AUCTIONSTATUSENUM.OPEN &&
                    <>
                        <span aria-hidden="true">•</span>
                        <span>{renderAuctionCountdown({suffix: ' left'})}</span>
                    </>
                }
                {displayStatus !== AUCTIONSTATUSENUM.OPEN && statusMessage &&
                    <>
                        <span aria-hidden="true">•</span>
                        <span>{statusMessage}</span>
                    </>
                }
            </div>
        );
    }

    function renderAuctionStatusDivider() {
        if (!showAuctionStatusDivider) {
            return <div className={"border-t mt-2 mb-3"}></div>;
        }

        const renderedTone = renderedCardBidStatus?.tone ?? cardBidStatusTone;
        const hasRenderedStatus = !!renderedCardBidStatus;
        const auctionStatusDividerClassName = getAuctionStatusDividerClassName(renderedTone);
        const auctionStatusChipClassName = getAuctionStatusChipClassName(renderedTone);

        return (
            <AnimateHeight
                height={hasRenderedStatus ? 'auto' : 1}
                duration={220}
                easing="cubic-bezier(0.22, 1, 0.36, 1)"
                className="mt-2 mb-3"
            >
                <div
                    className={classNames(
                        "auction-card-status-line relative flex min-h-6 items-center justify-center",
                        hasRenderedStatus ? "auction-card-status-line--expanded" : "auction-card-status-line--collapsed",
                        auctionStatusDividerClassName
                    )}
                >
                    <span
                        role="status"
                        aria-live="polite"
                        aria-atomic="true"
                        className={classNames(
                            "relative z-10 inline-flex h-5 max-w-[84%] items-center justify-center gap-1.5 overflow-hidden whitespace-nowrap rounded-full border bg-white px-2 text-[10px] font-bold uppercase leading-none text-slate-900 shadow-sm transition-all duration-200",
                            hasRenderedStatus ? "auction-card-status-chip" : "pointer-events-none min-w-[96px] opacity-0 shadow-none",
                            cardBidStatusPhase === 'enter' ? "auction-card-status-chip-enter" : null,
                            cardBidStatusPhase === 'exit' ? "auction-card-status-chip-exit" : null,
                            auctionStatusChipClassName
                        )}
                    >
                        {renderedCardBidStatus
                            ? <>
                                {renderCardBidStatusDot(renderedCardBidStatus.tone)}
                                <span className="block min-w-0 truncate text-center">{renderedCardBidStatus.label}</span>
                            </>
                            : null}
                    </span>
                </div>
            </AnimateHeight>
        );
    }

    return (
        <div
            key={id}
            className={classNames(
                "group relative flex flex-col overflow-hidden rounded-theme border border-gray-200 bg-white cursor-pointer grow",
                props.className
            )}>
            <ProductCardMediaCarousel
                product={liveProduct}
                id={id}
                href={href}
                aspectRatioClassName={aspectRatio(props)}
            />
            <a data-action="" href={href} className="flex flex-1 flex-col p-4">
                <h6 className="text-base font-bold leading-tight text-gray-900 !mb-1" data-title="">
                    {liveProduct?.title}
                </h6>
                    {liveProduct?.cardDetails && liveProduct?.cardDetails.length > 0 &&
                    <div className="grid grid-cols-2 gap-2 my-2">
                        {liveProduct.cardDetails.map((data, i) => {
                            const field = liveProduct?.userDefinedFieldsets.find(f => f.id === data.field);
                            const Icon = Icons.find(icon => icon.value === data.icon);

                            return (
                                <div className="flex items-start" key={data.field + i}>
                                    {data?.icon && <Icon.icon className={classNames(
                                        Icon.type === 'outline' ? "stroke-black" : null,
                                        Icon.type === 'solid' ? "fill-black" : null,
                                        "w-5 h-5 flex-none"
                                    )}/>}
                                    <span
                                        className="ml-1 text-gray-900 text-sm">{field?.prefix} <WebsiteFieldRenderer
                                        component={"DisplayView"}
                                        fields={[field]}
                                        values={liveProduct}/> {field?.suffix}</span>
                                </div>
                            )
                        })}
                    </div>
                }
                <div className="flex flex-1 flex-col">
                    {liveProduct.hidePriceOnWebsite && !liveProduct.salesEvent ? null : (
                        <div className={classNames(
                            "flex flex-1 flex-col",
                            showCardBidStatus ? "justify-center" : "justify-end"
                        )}>
                            {renderAuctionStatusDivider()}
                        </div>
                    )}
		                    <div className={"text-gray-900"}>
	                        {liveProduct.salesEvent
	                            ? <>
                                {displayStatus === AUCTIONSTATUSENUM.NOT_STARTED &&
	                                    <>Starts in <span className="font-semibold"><BidCountdown
                                        salesEvent={liveProduct.salesEvent}
                                        date={now + (secondsToStart * 1000)}
                                        onComplete={() => {
                                            // Add code to trigger the next bid count down seconds remaining.
                                            nowRef.current = Date.now();
                                            setNow(nowRef.current);
                                            setSecondsToStart(0)
                                            setStatus(AUCTIONSTATUSENUM.OPEN);
                                        }}/></span></>
                                }
                                {displayStatus === AUCTIONSTATUSENUM.OPEN && !isAuction &&
                                    <><span className="font-semibold">{renderAuctionCountdown()}</span> left</>
                                }
                                {statusMessage && !isAuction &&
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className={classNames("block font-semibold leading-tight", statusMessageClassName)}>{statusMessage}</span>
                                    </div>
                                }
                                {renderAuctionPriceSummary()}
                                {renderAuctionSecondarySummary()}
                                {showReserveStrip &&
                                    <div
                                        className={classNames(
                                            "auction-card-reserve-strip -mx-4 -mb-4 mt-2 flex items-center justify-center px-3 py-2 text-center",
                                            shouldAnimateReserveBadge ? "auction-card-reserve-strip-enter" : null
                                        )}
                                    >
                                        <span
                                            className={classNames(
                                                "auction-reserve-banner__text block leading-tight text-[11px] font-bold sm:text-[12px]",
                                                shouldAnimateReserveBadge ? "animate-pulse" : null
                                            )}
                                        >
                                            {reserveMetLabel}
                                        </span>
                                    </div>
                                }
	                            </>
	                            : !liveProduct?.hidePriceOnWebsite && <>
	                            {liveProduct?.discountedSellingPrice ? (<span className={"font-semibold mr-1"}>
	                                        <Currency.DisplayValue
                                            currency={context.currency}
                                            country={context.country}
                                            value={liveProduct?.discountedSellingPrice}
                                            emptyPlaceholder={""}/>
                                    </span>) : null}
                            <span
                                className={classNames(liveProduct?.discountedSellingPrice ? "line-through opacity-80 text-sm" : "font-semibold")}>
                                    {liveProduct?.sellingPrice
                                        ? <Currency.DisplayValue
                                            currency={context.currency}
                                            country={context.country}
                                            value={liveProduct?.sellingPrice}
                                            emptyPlaceholder={""}/>
                                        : "Price on Application"}
                                </span>
                            {liveProduct?.sellingPriceDisclaimer ? (<span className="ml-1 text-gray-500 text-sm">
                                        {liveProduct?.sellingPriceDisclaimer}
                                    </span>) : null}
                        </>
                        }
                    </div>
                </div>
            </a>
            {cardAction && (
                <div className="px-4 pb-4">
                    <ProductCardAction product={liveProduct} className="mt-1"/>
                </div>
            )}
        </div>
    )
})

export default Design1;
